import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Store, Plus, RefreshCw, Link2, Mail, Search } from 'lucide-react';
import { confirmDialog } from '../components/ConfirmHost';
import { RegistrationReviewDialog } from '../components/companies/RegistrationReview';
import { API, errText } from '../components/stores/storeShared';
import { StoreRow } from '../components/stores/StoreRow';
import { StorePagination } from '../components/stores/StorePagination';
import { StoreFormDialog } from '../components/stores/StoreFormDialog';
import { StoreSourcesDialog } from '../components/stores/StoreSourcesDialog';
import { StoreSyncDialog } from '../components/stores/StoreSyncDialog';
import { StoreExcelButtons } from '../components/stores/StoreExcelButtons';

const STATUSES = [['all', 'Tümü'], ['open', 'Açık'], ['closed', 'Kapalı'], ['kvkk_pending', 'KVKK onay bekleyen'], ['kvkk_ok', 'KVKK onaylı'], ['no_kvkk', 'KVKK yüklenmemiş'], ['no_contact', 'Yetkili bilgisi eksik'], ['account_deleted', 'Hesabı silinenler']];
const COLS = ['Mağaza / Kat', 'Yetkili', 'T.C.', 'Telefon', 'E-posta', 'KVKK / Talep', 'Hesap', ''];

export default function StoresPage() {
  const { user, canView, canTab } = useAuth();
  const allowed = canView('stores');
  const [data, setData] = useState({ items: [], total: 0 });
  const [locations, setLocations] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [q, setQ] = useState('');
  const [loc, setLoc] = useState('all');
  const [status, setStatus] = useState('all');
  const [sel, setSel] = useState(new Set());
  const [edit, setEdit] = useState(null);
  const [review, setReview] = useState(null);
  const [dlg, setDlg] = useState('');
  const load = () => axios.get(`${API}/stores`, { params: { page, page_size: pageSize, q, location_id: loc === 'all' ? '' : loc, status: status === 'all' ? '' : status } })
    .then(r => setData(r.data)).catch(() => toast.error('Mağazalar yüklenemedi'));
  useEffect(() => { if (allowed) axios.get(`${API}/locations`).then(r => setLocations(r.data)).catch(() => {}); }, [allowed]);
  useEffect(() => { if (!allowed) return; const t = setTimeout(load, 250); return () => clearTimeout(t); }, [allowed, page, pageSize, q, loc, status]);
  useEffect(() => setPage(1), [q, loc, status]);

  const pageIds = data.items.map(s => s.id);
  const allOnPage = pageIds.length > 0 && pageIds.every(id => sel.has(id));
  const toggleAll = (v) => setSel(p => { const n = new Set(p); pageIds.forEach(id => (v ? n.add(id) : n.delete(id))); return n; });
  const toggle = (id) => setSel(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const save = async (f, sendNow) => {
    let id;
    try { id = (edit.id ? await axios.put(`${API}/stores/${edit.id}`, f) : await axios.post(`${API}/stores`, f)).data.id; toast.success('Mağaza kaydedildi'); setEdit(null); } catch (e) { toast.error(errText(e)); return; }
    if (sendNow) {
      try {
        const r = (await axios.post(`${API}/stores/send-credentials`, { ids: [id] })).data;
        if (r.sent.length) toast.success(`Giriş e-postası gönderildi: ${f.contact_email}`);
        r.skipped.forEach(s => toast.warning(`Giriş e-postası gönderilmedi — ${s.name}: ${s.reason}`));
      } catch (e) { toast.error(`Mağaza kaydedildi ancak giriş e-postası gönderilemedi: ${errText(e)}`); }
    }
    load();
  };
  const mail = async (ids) => {
    if (!(await confirmDialog({ title: 'Giriş bilgisi gönderilsin mi?', description: `${ids.length} mağaza yetkilisine tek kullanımlık şifre içeren giriş e-postası gönderilecek. Daha önce gönderildiyse eski şifre geçersiz olur.`, confirmLabel: 'Gönder' }))) return;
    try {
      const r = (await axios.post(`${API}/stores/send-credentials`, { ids })).data;
      if (r.sent.length) toast.success(`${r.sent.length} yetkiliye giriş e-postası gönderildi`);
      r.skipped.forEach(s => toast.warning(`${s.name}: ${s.reason}`));
      setSel(new Set()); load();
    } catch (e) { toast.error(errText(e)); }
  };
  const toggleClosed = async (s) => {
    const closed = !s.store_closed;
    if (closed && !(await confirmDialog({ title: 'Mağaza kapalı olarak işaretlensin mi?', description: `"${s.name}" pasife alınır, yetkilisi giriş yapamaz. Geçmiş talepler korunur.`, confirmLabel: 'Evet' }))) return;
    try { await axios.put(`${API}/stores/${s.id}/closed`, null, { params: { closed } }); load(); } catch (e) { toast.error(errText(e)); }
  };
  const remove = async (s) => {
    if (!(await confirmDialog({ title: 'Mağaza silinsin mi?', description: `"${s.name}" ve yetkili hesabı silinecek.`, confirmLabel: 'Evet, Sil' }))) return;
    try { await axios.delete(`${API}/stores/${s.id}`); toast.success('Mağaza silindi'); load(); } catch (e) { toast.error(errText(e)); }
  };

  if (!allowed) return <p className="text-slate-500">Bu sayfa sadece Süper Admin ve İSG Uzmanı kullanıcılar içindir.</p>;
  return (
    <div className="space-y-5" data-testid="stores-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900"><Store className="h-6 w-6 text-orange-500" />Mağazalar / Kiracılar</h1>
          <p className="mt-1 max-w-2xl text-slate-500">AVM web sitesinden mağaza listesini senkronize edin, yetkili bilgilerini girin ve giriş bilgilerini e-postayla gönderin. KVKK onaylanana kadar her mağaza en fazla 3 talep gönderebilir.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setDlg('sources')} data-testid="store-sources-btn"><Link2 className="h-4 w-4 mr-1" />Kaynak Linkler</Button>
          <Button variant="outline" onClick={() => setDlg('sync')} data-testid="store-sync-btn"><RefreshCw className="h-4 w-4 mr-1" />Senkronize Et</Button>
          <StoreExcelButtons onImported={load} />
          <Button onClick={() => setEdit({})} className="bg-slate-900 hover:bg-slate-800" data-testid="store-add-btn"><Plus className="h-4 w-4 mr-1" />Yeni Mağaza</Button>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative w-72"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" /><Input className="pl-9" placeholder="Mağaza, yetkili, e-posta, T.C. ara..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="store-search-input" /></div>
        <Select value={loc} onValueChange={setLoc}><SelectTrigger className="w-48" data-testid="store-location-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tüm lokasyonlar</SelectItem>{locations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent></Select>
        <Select value={status} onValueChange={setStatus}><SelectTrigger className="w-52" data-testid="store-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>{STATUSES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select>
        {sel.size > 0 && <Button onClick={() => mail([...sel])} className="ml-auto bg-sky-600 hover:bg-sky-700" data-testid="store-bulk-mail-btn"><Mail className="h-4 w-4 mr-1" />Seçilenlere giriş e-postası gönder ({sel.size})</Button>}
      </div>
      <div className="overflow-hidden rounded-xl border bg-white">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
              <tr><th className="w-10 px-3 py-2.5"><Checkbox checked={allOnPage} onCheckedChange={toggleAll} title="Bu sayfadakilerin tümünü seç" data-testid="store-check-all" /></th>{COLS.map(c => <th key={c} className="px-3 py-2.5">{c}</th>)}</tr>
            </thead>
            <tbody>
              {data.items.length === 0 ? <tr><td colSpan={9} className="p-10 text-center text-slate-500" data-testid="store-empty">{status !== 'all' || loc !== 'all' || q ? 'Filtreye uyan mağaza yok.' : 'Kayıt bulunamadı. "Kaynak Linkler" ile web sitesi ekleyip senkronize edin veya Excel ile yükleyin.'}</td></tr>
                : data.items.map(s => <StoreRow key={s.id} s={s} checked={sel.has(s.id)} onCheck={() => toggle(s.id)} onEdit={() => setEdit(s)} onMail={() => mail([s.id])}
                  onToggleClosed={() => toggleClosed(s)} onDelete={() => remove(s)} onKvkk={() => setReview({ ...s, registration_status: 'approved' })} />)}
            </tbody>
          </table>
        </div>
        <StorePagination page={page} pageSize={pageSize} total={data.total} setPage={setPage} setPageSize={setPageSize} />
      </div>
      <StoreFormDialog store={edit} locations={locations} onClose={() => setEdit(null)} onSave={save} />
      <StoreSourcesDialog open={dlg === 'sources'} onClose={() => setDlg('')} locations={locations} />
      <StoreSyncDialog open={dlg === 'sync'} onClose={() => setDlg('')} onApplied={load} />
      {review && <RegistrationReviewDialog company={review} docKeys={['kvkk_taahhutnamesi', 'aydinlatma_yukumlulugu']} onClose={() => { setReview(null); load(); }} onDone={() => { setReview(null); load(); }} onRefresh={load} />}
    </div>
  );
}
